import { ROUTES } from '@dealstack/frontend/app/pages/Routing/routes';
import { BreadcrumbDropdown } from '@dealstack/frontend/shared/components/BreadcrumbDropdown';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { SummaryDealTct } from '@dealstack/shared/lib/generated';
import { memo, useMemo } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { MantineMenuItemProps } from '@dealstack/frontend/shared/components/MantineMenu/MantineMenu.types';

interface Props {
	placeholderName?: string;
	currentDealId?: string;
	deals: SummaryDealTct[];
	appendToRoute?: string;
}

const DealDropdown = ({ placeholderName, currentDealId, deals, appendToRoute = '' }: Props) => {
	const tCommonUi = useTranslationCommonUi();
	const navigate = useNavigate();

	const currentDealName = useMemo(() => {
		if (!currentDealId && placeholderName) {
			return placeholderName;
		}

		return deals?.find((d) => d.id === currentDealId)?.name;
	}, [deals, currentDealId]);

	const items: MantineMenuItemProps[] | undefined = deals?.map((item) => {
		const isSelected = item.id === currentDealId;
		const path = generatePath(`${ROUTES.DEAL}${appendToRoute}`, { dealId: item.id });
		return {
			key: item.id,
			label: item.name,
			className: isSelected ? 'selected' : '',
			onClick: () => navigate(path),
			rightSection: isSelected ? <Icon variant="tick" size="lg" /> : undefined,
			dataTestId: item.name,
		};
	});

	const bottomSectionItems: MantineMenuItemProps[] = [
		{
			key: 'new-deal',
			label: tCommonUi('breadcrumbs.deal_dropdown.create_label'),
			onClick: () => navigate(ROUTES.DEAL_CREATE),
			icon: <Icon variant="add" size="lg" />,
		},
	];

	const targetHref = generatePath(ROUTES.DEAL, { dealId: currentDealId ?? '' });

	return (
		<BreadcrumbDropdown
			targetHref={targetHref}
			targetTitle={currentDealName}
			items={items}
			bottomSectionItems={bottomSectionItems}
			breadcrumbType="deal"
		/>
	);
};
const _DealDropdownExport = memo(DealDropdown);

export { _DealDropdownExport as DealDropdown };
